import { origemAtual } from "@/lib/navegacao";
import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAtualizarFechamento, type FechamentoDoMes } from "@/hooks/use-equipe";
import { formatarBRL, calcLiquidoFechamento, calcAvisoTetoVariavel, formatarAvisoTetoVariavel } from "@shared/equipe-fechamento";
import { msgDoErro } from "./msg-do-erro";

/**
 * O fechamento de UMA pessoa, editável enquanto ABERTO (variável, descontos,
 * observações) e somente leitura depois de FECHADO. Movido de
 * `DialogosFase2.tsx` (Financeiro v3): é o mesmo bloco do extrato da aba Mês e
 * do diálogo antigo, até o Ciclo 2 apagar o diálogo.
 *
 * `embutido` = dentro do extrato (sem o nome — o cabeçalho do sheet já o tem).
 */
export function LinhaFechamento({
  f, adicionalCentavos, bloqueado, embutido,
}: { f: FechamentoDoMes; adicionalCentavos: number; bloqueado: boolean; embutido?: boolean }) {
  const atualizar = useAtualizarFechamento();
  const { toast } = useToast();
  const fechado = f.status === "FECHADO";

  const [variavel, setVariavel] = useState(f.variavelApurada ?? "0");
  const [obs, setObs] = useState(f.variavelObs ?? "");
  const [descontos, setDescontos] = useState(f.descontos ?? "0");
  const [descontosObs, setDescontosObs] = useState(f.descontosObs ?? "");

  // Prévia local — o valor gravado é sempre o que o SERVIDOR recalcula.
  const previa = calcLiquidoFechamento({
    salarioFixo: f.salarioFixo,
    adiantamentosPagos: f.adiantamentosPagos,
    variavelApurada: variavel,
    descontos,
  });

  // §3 — aviso (nunca bloqueio) de estouro do teto: variável apurada + adicionais
  // do mês. `null` quando a pessoa não tem teto — sem ruído no caso comum.
  const avisoTeto = calcAvisoTetoVariavel({
    variavelApurada: variavel,
    adicionalCentavos,
    variavelMaxMensal: f.variavelMaxMensal,
  });

  const salvar = async (fechar: boolean) => {
    try {
      await atualizar.mutateAsync({
        id: f.id,
        data: {
          variavelApurada: variavel,
          variavelObs: obs.trim() || null,
          descontos,
          descontosObs: descontosObs.trim() || null,
          ...(fechar ? { status: "FECHADO" } : {}),
        },
      });
      toast({ title: fechar ? "Mês fechado — marcado como pago." : "Fechamento salvo." });
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao salvar o fechamento"), variant: "destructive" });
    }
  };

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${fechado ? "bg-muted" : "bg-card"}`}>
      {!embutido && (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <Link href={`/equipe/ficha/${f.funcionarioId}?competencia=${f.competencia}`} state={origemAtual()} className="text-sm font-semibold truncate hover:underline block">
              {f.apelido ?? f.nome}
            </Link>
            <p className="text-[11px] text-muted-foreground">
              {f.diasRegistro ?? 0} registro{(f.diasRegistro ?? 0) === 1 ? "" : "s"}
              {(f.diasFalta ?? 0) > 0 && ` · ${f.diasFalta} falta${f.diasFalta === 1 ? "" : "s"}`}
              {" · "}{f.formaPagamento === "QUINZENAL" ? "quinzenal" : "1×/mês"}
            </p>
          </div>
          {fechado && (
            <Badge variant="outline" className="shrink-0 bg-muted text-foreground text-[10px]">
              <Lock className="h-3 w-3 mr-1" />Fechado
            </Badge>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><span className="text-muted-foreground">Fixo</span><br /><strong>{formatarBRL(f.salarioFixo)}</strong></div>
        <div><span className="text-muted-foreground">Adiantado</span><br /><strong>− {formatarBRL(f.adiantamentosPagos)}</strong></div>
      </div>

      {!fechado ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Variável (teto {formatarBRL(f.variavelMaxMensal)})</Label>
              <Input type="number" step="0.01" inputMode="decimal" value={variavel} onChange={e => setVariavel(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Descontos</Label>
              <Input type="number" step="0.01" inputMode="decimal" value={descontos} onChange={e => setDescontos(e.target.value)} />
            </div>
          </div>
          {avisoTeto && adicionalCentavos > 0 && (
            <p className={`text-[11px] ${avisoTeto.estourou ? "text-atencao" : "text-muted-foreground"}`}>
              {formatarAvisoTetoVariavel(avisoTeto)}
            </p>
          )}
          <div className="space-y-1">
            <Label className="text-xs">De onde veio a variável</Label>
            <Input
              value={obs}
              onChange={e => setObs(e.target.value)}
              placeholder="auditoria de agosto / sem auditoria"
            />
            {Number(variavel) > 0 && !obs.trim() && (
              <p className="text-[11px] text-erro">Obrigatório quando há variável.</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Motivo dos descontos</Label>
            <Input
              value={descontosObs}
              onChange={e => setDescontosObs(e.target.value)}
              placeholder="lançamentos de desconto da competência"
            />
          </div>
          <p className="text-sm">Líquido: <strong>{formatarBRL(previa)}</strong></p>
          {bloqueado && (
            <p className="text-[11px] text-erro">
              Resolva a pendência de adiantamento antes de marcar o mês desta pessoa como pago.
            </p>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1" disabled={atualizar.isPending} onClick={() => salvar(false)}>
              Salvar
            </Button>
            {/* TRAVA — o servidor recusa igual (409 ADIANTAMENTO_PENDENTE); aqui
                o botão já sai desabilitado pra não oferecer um caminho que vai
                falhar, e pra deixar claro que a saída é o bloco vermelho. */}
            <Button size="sm" className="flex-1" disabled={atualizar.isPending || bloqueado} onClick={() => salvar(true)}>
              Pago
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1 text-xs">
          <p className="text-sm">Líquido: <strong>{formatarBRL(f.liquidoFinal)}</strong></p>
          <p className="text-muted-foreground">
            variável {formatarBRL(f.variavelApurada)}{f.variavelObs ? ` (${f.variavelObs})` : ""}
            {" · "}descontos {formatarBRL(f.descontos)}{f.descontosObs ? ` (${f.descontosObs})` : ""}
          </p>
        </div>
      )}
    </div>
  );
}
